import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Badge,
    Box,
    Button,
    Checkbox,
    HStack,
    Input,
    Link,
    Menu,
    VStack,
    Text,
    Table,
    Spinner,
    IconButton,
    Select,
    Popover,
    Portal,
    createListCollection,
} from "@chakra-ui/react";
import { HiOutlineCpuChip, HiArrowUp, HiArrowDown, HiStar, HiEllipsisVertical } from "react-icons/hi2";
import {
    formatMetadataDisplay,
    getTimelineBadgeProps,
    getTimelineEventDescription,
    parseMetadata,
} from "./components/candidate/timelineHelpers";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { ConversationService } from "@/services/ConversationService";
import { ModelService } from "@/services/ModelService";
import { CandidateService } from "@/services/CandidateService";
import { RecruitmentStatusBadge } from "@/pages/models/RecruitmentStatusBadge";
import InfoField from "./components/InfoField";
import CandidateStatusBadge from "./components/CandidateStatusBadge";
import MobileFiltersPanel from "./components/MobileFiltersPanel";
import DateInput from "./components/DateInput";
import BulkActionsBar from "./components/BulkActionsBar";
import BulkChangeStatusModal from "./components/candidate/BulkChangeStatusModal";
import BulkEmailModal from "./components/candidate/BulkEmailModal";
import { toaster } from "@/components/ui/toaster";
import useCandidateSingleActions from "./hooks/useCandidateSingleActions";
import dayjs from "dayjs";
import { ScoreBar, evaluationStatusFromApi, SCORING_ENABLED } from "@/scoringUi";

/** Converts a CandidateEvaluationSummaryResponse (PascalCase status from the API) into the shape @/scoringUi's ScoreBadge/getScoreBucket expect. */
const toEvaluationBucketShape = (evaluation) =>
    evaluation ? { scorePercent: evaluation.scorePercent, status: evaluationStatusFromApi(evaluation.status) } : null;

const RecruitmentCandidatesPage = () => {
    const { t: tJobOffer } = useTranslation("jobOffer");
    const { t } = useTranslation("models");
    const { id, recruitmentId } = useParams();
    const navigate = useNavigate();

    const [recruitment, setRecruitment] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [isRecruitmentLoading, setIsRecruitmentLoading] = useState(true);
    const [isCandidatesLoading, setIsCandidatesLoading] = useState(true);
    const [isXlsxLoading, setIsXlsxLoading] = useState(false);
    const [isFavLoading, setIsFavLoading] = useState(false);
    const [candFavLoadingId, setCandFavLoadingId] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [statusFilter, setStatusFilter] = useState([]);
    const [sortBy, setSortBy] = useState("createdat");
    const [sortDir, setSortDir] = useState("desc");
    const [showFavoriteOnly, setShowFavoriteOnly] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [createdFrom, setCreatedFrom] = useState("");
    const [createdTo, setCreatedTo] = useState("");
    const [scoreMin, setScoreMin] = useState("");
    const [scoreMax, setScoreMax] = useState("");
    const [disqualifiedOnly, setDisqualifiedOnly] = useState(false);
    const [noScoreOnly, setNoScoreOnly] = useState(false);
    const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
    const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
    const [isBulkStatusLoading, setIsBulkStatusLoading] = useState(false);
    const [isBulkEmailLoading, setIsBulkEmailLoading] = useState(false);
    const [isSelectAllLoading, setIsSelectAllLoading] = useState(false);
    const [candidatesRefreshKey, setCandidatesRefreshKey] = useState(0);
    const [mockCandidate, setMockCandidate] = useState(null);
    const [mockStep, setMockStep] = useState(0);

    const { openStatusModal, openEmailModal, openNoteModal, modals: singleActionModals } = useCandidateSingleActions({
        t,
        onStatusChanged: (candId, updated) =>
            setCandidates(prev => prev.map(c => c.id === candId ? { ...c, status: updated.status } : c)),
    });

    // Dev-only preview helper: cycles a single client-side-only candidate through the fixed
    // score buckets so the score bar/badge colors can be sanity-checked without real data.
    // Never sent to the API, never persisted -- resets on page refresh.
    const MOCK_SCORE_SEQUENCE = [86, 74, 58, 42, 0, null];
    const handleAddMockCandidate = () => {
        if (!mockCandidate) {
            const scorePercent = MOCK_SCORE_SEQUENCE[0];
            setMockCandidate({
                id: "mock-preview",
                firstname: "Testowy",
                lastname: "Kandydat (mock)",
                status: "Created",
                isFavourite: false,
                email: "mock@example.com",
                phoneNumber: null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                evaluation: scorePercent == null ? null : { scorePercent, status: "Scored" },
            });
            setMockStep(0);
            return;
        }
        const nextStep = (mockStep + 1) % MOCK_SCORE_SEQUENCE.length;
        const scorePercent = MOCK_SCORE_SEQUENCE[nextStep];
        setMockStep(nextStep);
        setMockCandidate((prev) => ({
            ...prev,
            evaluation: scorePercent == null ? null : { scorePercent, status: "Scored" },
        }));
    };
    const displayCandidates = mockCandidate ? [mockCandidate, ...candidates] : candidates;

    const evaluationCollection = createListCollection({
        items: [
            { label: t("candidatePage.filterDisqualifiedOnly"), value: "disqualified" },
            { label: t("candidatePage.filterNoScore"), value: "noScore" },
        ],
    });
    const evaluationFilterValue = [
        ...(disqualifiedOnly ? ["disqualified"] : []),
        ...(noScoreOnly ? ["noScore"] : []),
    ];

    const statusCollection = createListCollection({
        items: [
            { label: t("candidateStatus.initialized"), value: "Initialized" },
            { label: t("candidateStatus.created"), value: "Created" },
            { label: t("candidateStatus.finalized"), value: "Finalized" },
            { label: t("candidateStatus.qualifiedfornextstage"), value: "QualifiedForNextStage" },
            { label: t("candidateStatus.furtherselection"), value: "FurtherSelection" },
            { label: t("candidateStatus.readytohire"), value: "ReadyToHire" },
            { label: t("candidateStatus.hired"), value: "Hired" },
            { label: t("candidateStatus.rejected"), value: "Rejected" },
            { label: t("candidateStatus.candidateresigned"), value: "CandidateResigned" },
        ],
    });

    useSetPageHeader(t("candidatePage.headerTitle"), "", <HiOutlineCpuChip />);

    useEffect(() => {
        setIsRecruitmentLoading(true);
        ConversationService.getRecruitmentById(id, recruitmentId)
            .then(setRecruitment)
            .finally(() => setIsRecruitmentLoading(false));
    }, [id, recruitmentId]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        setIsCandidatesLoading(true);
        const statuses = statusFilter;
        CandidateService.getByRecruitment(
            recruitmentId, page, pageSize, statuses,
            showFavoriteOnly || null, sortBy, sortDir, debouncedSearch || null,
            createdFrom || null, createdTo || null,
            scoreMin === "" ? null : Number(scoreMin), scoreMax === "" ? null : Number(scoreMax),
            disqualifiedOnly || null, noScoreOnly || null
        )
            .then((data) => {
                setCandidates(data?.items ?? []);
                setTotalCount(data?.totalCount ?? 0);
                setSelectedIds(new Set());
            })
            .finally(() => setIsCandidatesLoading(false));
    }, [recruitmentId, page, pageSize, statusFilter, sortBy, sortDir, showFavoriteOnly, debouncedSearch, createdFrom, createdTo, scoreMin, scoreMax, disqualifiedOnly, noScoreOnly, candidatesRefreshKey]);

    const isAllSelected = candidates.length > 0 && selectedIds.size === candidates.length;
    const isIndeterminate = selectedIds.size > 0 && selectedIds.size < candidates.length;

    const toggleSelect = (candId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(candId) ? next.delete(candId) : next.add(candId);
            return next;
        });
    };

    const toggleAll = () => {
        if (isAllSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(candidates.map((c) => c.id)));
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("desc");
        }
        setPage(1);
    };

    const handleDownloadXlsx = async () => {
        setIsXlsxLoading(true);
        try {
            const ids = selectedIds.size > 0 ? Array.from(selectedIds) : [];
            const statuses = statusFilter;
            const response = await CandidateService.exportXlsx(
                recruitmentId, ids, statuses, showFavoriteOnly || null, sortBy, sortDir, debouncedSearch || null
            );
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `kandydaci_${recruitment.jobTitle.replace(' ', '_')}_${dayjs().format("YYYY-MM-DD")}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setIsXlsxLoading(false);
        }
    };

    const handleToggleFavorite = async () => {
        setIsFavLoading(true);
        const updated = await ModelService.toggleFavorite(recruitmentId);
        setRecruitment(updated);
        setIsFavLoading(false);
    };

    const handleToggleCandidateFavorite = async (e, candId) => {
        e.stopPropagation();
        if (candId === "mock-preview") {
            setMockCandidate((prev) => (prev ? { ...prev, isFavourite: !prev.isFavourite } : prev));
            return;
        }
        setCandFavLoadingId(candId);
        const updated = await CandidateService.toggleFavorite(candId);
        setCandidates((prev) =>
            prev.map((c) => c.id === candId ? { ...c, isFavourite: updated.isFavourite } : c)
        );
        setCandFavLoadingId(null);
    };

    const handleSelectAllFilter = async () => {
        setIsSelectAllLoading(true);
        try {
            const statuses = statusFilter;
            const data = await CandidateService.getByRecruitment(
                recruitmentId, 1, totalCount, statuses,
                showFavoriteOnly || null, sortBy, sortDir, debouncedSearch || null,
                createdFrom || null, createdTo || null
            );
            setSelectedIds(new Set((data?.items ?? []).map(c => c.id)));
        } finally {
            setIsSelectAllLoading(false);
        }
    };

    const handleBulkChangeStatus = async (status, note) => {
        setIsBulkStatusLoading(true);
        try {
            const result = await CandidateService.bulkChangeStatus(Array.from(selectedIds), status, note);
            toaster.create({
                type: result.failureCount > 0 ? "warning" : "success",
                title: t("candidatePage.bulkChangeStatusSuccess", {
                    success: result.successCount,
                    total: result.successCount + result.failureCount,
                }),
            });
            setShowBulkStatusModal(false);
            setSelectedIds(new Set());
            setCandidatesRefreshKey(k => k + 1);
        } catch {
            toaster.create({ type: "error", title: t("candidatePage.bulkChangeStatusError") });
        } finally {
            setIsBulkStatusLoading(false);
        }
    };

    const handleBulkSendEmail = async (subject, body, files) => {
        setIsBulkEmailLoading(true);
        try {
            const result = await CandidateService.bulkSendEmail(Array.from(selectedIds), subject, body, files);
            toaster.create({
                type: result.failureCount > 0 ? "warning" : "success",
                title: t("candidatePage.bulkEmailSuccess", {
                    success: result.successCount,
                    total: result.successCount + result.failureCount,
                }),
            });
            setShowBulkEmailModal(false);
        } catch {
            toaster.create({ type: "error", title: t("candidatePage.bulkEmailError") });
        } finally {
            setIsBulkEmailLoading(false);
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <>
        <VStack w="full" py={6} gap={6} align="flex-start">
            {isRecruitmentLoading ? (
                <VStack w="full" py={8}
                    align="center"
                    justify="center"
                    bg="rgba(0,0,0,0.1)"
                    borderRadius={12}
                    minH={100}>
                    <Spinner color="brand.400" size="lg" />
                </VStack>
            ) : recruitment && (
                <Box
                    w="full"
                    bg="bg.card"
                    border="1px solid"
                    borderColor="border.primary"
                    borderRadius={12}
                    p={5}
                >
                    <HStack justify="space-between" align="flex-start" mb={4} flexWrap="wrap" gap={2}>
                        <HStack>
                            <Text fontWeight={700} fontSize="lg" color="white">{recruitment.jobTitle}</Text>
                            <IconButton
                                variant="ghost"
                                size="sm"
                                aria-label="Ulubione"
                                color={recruitment?.isFavorite ? "yellow.400" : "gray.500"}
                                loading={isFavLoading}
                                onClick={handleToggleFavorite}
                            >
                                <HiStar />
                            </IconButton>
                        </HStack>
                        <HStack gap={2} flexWrap="wrap">
                            {SCORING_ENABLED && (
                                <Button variant="ghost" size="sm" color="text.grey" onClick={handleAddMockCandidate} w={{ base: "full", md: "auto" }}>
                                    {mockCandidate ? "Testowy kandydat: kolejny wynik" : "Dodaj testowego kandydata"}
                                </Button>
                            )}
                            <Button variant="outlined" size="sm" onClick={handleDownloadXlsx} loading={isXlsxLoading} w={{ base: "full", md: "auto" }}>
                                {selectedIds.size > 0
                                    ? t("candidatePage.downloadXlsxSelected", { count: selectedIds.size })
                                    : t("candidatePage.downloadXlsx")}
                            </Button>
                        </HStack>
                    </HStack>
                    <HStack gap={8} align="flex-start" flexWrap="wrap">
                        <InfoField label={t("recruitmentDetailPage.fieldPosition")}>
                            <Text color="white" fontSize="md" fontWeight={700}>
                                {recruitment.jobTitle}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldStatus")}>
                            <RecruitmentStatusBadge status={recruitment.status} />
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldCreatedAt")}>
                            <Text color="white" fontSize="sm">
                                {new Date(recruitment.createdAtUtc).toLocaleDateString('pl-PL')}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldCompany")}>
                            <Text color="white" fontSize="sm">
                                {recruitment.companyName}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldLocation")}>
                            <Text color="white" fontSize="sm">
                                {recruitment.location}
                            </Text>
                        </InfoField>
                    </HStack>
                </Box>
            )}

            <VStack w="full" align="flex-start" gap={3} pb={selectedIds.size > 0 ? 20 : 0}>
                <MobileFiltersPanel
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    statusFilter={statusFilter}
                    onStatusChange={(val) => { setStatusFilter(val); setPage(1); }}
                    statusCollection={statusCollection}
                    showFavoriteOnly={showFavoriteOnly}
                    onFavoriteToggle={() => { setShowFavoriteOnly(v => !v); setPage(1); }}
                    createdFrom={createdFrom}
                    onCreatedFromChange={(val) => { setCreatedFrom(val); setPage(1); }}
                    createdTo={createdTo}
                    onCreatedToChange={(val) => { setCreatedTo(val); setPage(1); }}
                />
                <HStack w="full" justify="space-between" align="center">
                    <HStack>
                        <Text fontWeight={700} fontSize="lg" color="white">
                            {t("candidatePage.candidatesListTitle")}
                        </Text>
                        <IconButton
                            display={{ base: 'none', md: 'flex' }}
                            variant="ghost"
                            size="sm"
                            aria-label="showFavoriteOnly"
                            color={showFavoriteOnly ? "yellow.400" : "gray.500"}
                            onClick={() => { setShowFavoriteOnly(v => !v); setPage(1); }}
                        >
                            <HiStar />
                        </IconButton>
                    </HStack>
                    <HStack gap={2} display={{ base: 'none', md: 'flex' }}>
                        <Input
                            placeholder={t("recruitmentDetailPage.searchPlaceholder")}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size="md"
                            w="200px"
                            bg="bg.card"
                            border="1px solid"
                            borderColor="border.primary"
                            borderRadius="md"
                            color="white"
                            _placeholder={{ color: "text.grey" }}
                        />
                        <Select.Root
                            collection={statusCollection}
                            value={statusFilter}
                            onValueChange={(e) => { setStatusFilter(e.value); setPage(1); }}
                            multiple
                            size="md"
                            w={'200px'}
                        >
                            <Select.HiddenSelect />
                            <Select.Control>
                                <Select.Trigger
                                    h="10"
                                    bg="bg.card"
                                    border="1px solid"
                                    borderColor="border.primary"
                                    borderRadius="md"
                                    fontSize="sm"
                                    color="white"
                                >
                                    <Select.ValueText color="white" placeholder={t("candidateStatus.all")} />
                                </Select.Trigger>
                                <Select.IndicatorGroup>
                                    <Select.Indicator color="white" />
                                </Select.IndicatorGroup>
                            </Select.Control>
                            <Select.Positioner>
                                <Select.Content bg="bg.card" borderColor="border.primary">
                                    {statusCollection.items.map((opt) => (
                                        <Select.Item item={opt} key={opt.value} color="white">
                                            {opt.label}
                                            <Select.ItemIndicator />
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Select.Root>
                        <DateInput
                            placeholder={t("candidatePage.filterCreatedFrom")}
                            value={createdFrom}
                            onChange={(e) => { setCreatedFrom(e.target.value); setPage(1); }}
                            size="md"
                            w="160px"
                            bg="bg.card"
                            border="1px solid"
                            borderColor="border.primary"
                            borderRadius="md"
                            color="white"
                            _placeholder={{ color: "text.grey" }}
                        />
                        <DateInput
                            placeholder={t("candidatePage.filterCreatedTo")}
                            value={createdTo}
                            onChange={(e) => { setCreatedTo(e.target.value); setPage(1); }}
                            size="md"
                            w="160px"
                            bg="bg.card"
                            border="1px solid"
                            borderColor="border.primary"
                            borderRadius="md"
                            color="white"
                            _placeholder={{ color: "text.grey" }}
                        />
                        {SCORING_ENABLED && (
                            <>
                                <Input
                                    placeholder={t("candidatePage.filterScoreMin")}
                                    value={scoreMin}
                                    onChange={(e) => { setScoreMin(e.target.value); setPage(1); }}
                                    type="number"
                                    size="md"
                                    w="90px"
                                    bg="bg.card"
                                    border="1px solid"
                                    borderColor="border.primary"
                                    borderRadius="md"
                                    color="white"
                                    _placeholder={{ color: "text.grey" }}
                                />
                                <Input
                                    placeholder={t("candidatePage.filterScoreMax")}
                                    value={scoreMax}
                                    onChange={(e) => { setScoreMax(e.target.value); setPage(1); }}
                                    type="number"
                                    size="md"
                                    w="90px"
                                    bg="bg.card"
                                    border="1px solid"
                                    borderColor="border.primary"
                                    borderRadius="md"
                                    color="white"
                                    _placeholder={{ color: "text.grey" }}
                                />
                                <Select.Root
                                    collection={evaluationCollection}
                                    value={evaluationFilterValue}
                                    onValueChange={(e) => {
                                        setDisqualifiedOnly(e.value.includes("disqualified"));
                                        setNoScoreOnly(e.value.includes("noScore"));
                                        setPage(1);
                                    }}
                                    multiple
                                    size="md"
                                    w={'160px'}
                                >
                                    <Select.HiddenSelect />
                                    <Select.Control>
                                        <Select.Trigger
                                            h="10"
                                            bg="bg.card"
                                            border="1px solid"
                                            borderColor="border.primary"
                                            borderRadius="md"
                                            fontSize="sm"
                                            color="white"
                                        >
                                            <Select.ValueText color="white" placeholder={t("candidatePage.filterEvaluationPlaceholder")} />
                                        </Select.Trigger>
                                        <Select.IndicatorGroup>
                                            <Select.Indicator color="white" />
                                        </Select.IndicatorGroup>
                                    </Select.Control>
                                    <Select.Positioner>
                                        <Select.Content bg="bg.card" borderColor="border.primary">
                                            {evaluationCollection.items.map((opt) => (
                                                <Select.Item item={opt} key={opt.value} color="white">
                                                    {opt.label}
                                                    <Select.ItemIndicator />
                                                </Select.Item>
                                            ))}
                                        </Select.Content>
                                    </Select.Positioner>
                                </Select.Root>
                            </>
                        )}
                    </HStack>
                </HStack>

                {selectedIds.size > 0 && (
                    <BulkActionsBar
                        selectedCount={selectedIds.size}
                        totalCount={totalCount}
                        isAllSelected={isAllSelected}
                        totalPages={totalPages}
                        onSelectAll={handleSelectAllFilter}
                        onDeselectAll={() => setSelectedIds(new Set())}
                        onChangeStatus={() => setShowBulkStatusModal(true)}
                        onSendEmail={() => setShowBulkEmailModal(true)}
                        onExport={handleDownloadXlsx}
                        isXlsxLoading={isXlsxLoading}
                        isSelectAllLoading={isSelectAllLoading}
                    />
                )}

                <Box w="full" position="relative">
                    {isCandidatesLoading && (
                        <VStack
                            position="absolute"
                            inset={0}
                            zIndex={1}
                            align="center"
                            justify="center"
                            bg="rgba(0,0,0,0.1)"
                            borderRadius={12}
                            minH={100}
                        >
                            <Spinner color="brand.400" size="lg" />
                        </VStack>
                    )}

                    {/* Desktop table */}
                    <Box
                        display={{ base: 'none', md: 'block' }}
                        w="full"
                        border="1px solid"
                        borderColor="border.primary"
                        borderRadius={12}
                        overflowX="auto"
                        opacity={isCandidatesLoading ? 0.4 : 1}
                        transition="opacity 0.15s"
                    >
                        <Table.Root minW="1200px">
                            <Table.Header>
                                <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                    <Table.ColumnHeader py={3} px={5} border="none" w="40px">
                                        <Checkbox.Root
                                            checked={isIndeterminate ? "indeterminate" : isAllSelected}
                                            onCheckedChange={toggleAll}
                                            aria-label="Select all"
                                        >
                                            <Checkbox.HiddenInput />
                                            <Checkbox.Control>
                                                <Checkbox.Indicator />
                                            </Checkbox.Control>
                                        </Checkbox.Root>
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader py={3} px={2} border="none" w="40px" />
                                    <Table.ColumnHeader
                                        color="text.grey" fontWeight={600} fontSize="xs"
                                        textTransform="uppercase" letterSpacing="wider"
                                        py={3} px={5} border="none"
                                    >
                                        {t("candidatePage.columnFullName")}
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader
                                        color="text.grey" fontWeight={600} fontSize="xs"
                                        textTransform="uppercase" letterSpacing="wider"
                                        py={3} px={5} border="none"
                                    >
                                        {t("candidatePage.columnStatus")}
                                    </Table.ColumnHeader>
                                    {SCORING_ENABLED && (
                                        <Table.ColumnHeader
                                            color="text.grey" fontWeight={600} fontSize="xs"
                                            textTransform="uppercase" letterSpacing="wider"
                                            py={3} px={5} border="none"
                                            cursor="pointer"
                                            onClick={() => handleSort("score")}
                                            _hover={{ color: "white" }}
                                        >
                                            <HStack gap={1}>
                                                <Text>{t("candidatePage.columnScore")}</Text>
                                                {sortBy === "score" && (sortDir === "asc" ? <HiArrowUp size={12} /> : <HiArrowDown size={12} />)}
                                            </HStack>
                                        </Table.ColumnHeader>
                                    )}
                                    <Table.ColumnHeader
                                        color="text.grey" fontWeight={600} fontSize="xs"
                                        textTransform="uppercase" letterSpacing="wider"
                                        py={3} px={5} border="none"
                                    >
                                        {t("candidatePage.columnEmail")}
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader
                                        color="text.grey" fontWeight={600} fontSize="xs"
                                        textTransform="uppercase" letterSpacing="wider"
                                        py={3} px={5} border="none"
                                    >
                                        {t("candidatePage.columnPhoneNumber")}
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader
                                        color="text.grey" fontWeight={600} fontSize="xs"
                                        textTransform="uppercase" letterSpacing="wider"
                                        py={3} px={5} border="none"
                                    >
                                        {t("candidatePage.columnLanguage")}
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader
                                        color="text.grey" fontWeight={600} fontSize="xs"
                                        textTransform="uppercase" letterSpacing="wider"
                                        py={3} px={5} border="none"
                                        cursor="pointer"
                                        onClick={() => handleSort("createdat")}
                                        _hover={{ color: "white" }}
                                    >
                                        <HStack gap={1}>
                                            <Text>{t("candidatePage.columnCreatedAt")}</Text>
                                            {sortBy === "createdat" && (sortDir === "asc" ? <HiArrowUp size={12} /> : <HiArrowDown size={12} />)}
                                        </HStack>
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader
                                        color="text.grey" fontWeight={600} fontSize="xs"
                                        textTransform="uppercase" letterSpacing="wider"
                                        py={3} px={5} border="none"
                                        cursor="pointer"
                                        onClick={() => handleSort("updatedat")}
                                        _hover={{ color: "white" }}
                                    >
                                        <HStack gap={1}>
                                            <Text>{t("candidatePage.columnLastActivity")}</Text>
                                            {sortBy === "updatedat" && (sortDir === "asc" ? <HiArrowUp size={12} /> : <HiArrowDown size={12} />)}
                                        </HStack>
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader
                                        color="text.grey" fontWeight={600} fontSize="xs"
                                        textTransform="uppercase" letterSpacing="wider"
                                        py={3} px={5} border="none"
                                    >
                                        {t("candidatePage.columnLastNote")}
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader
                                        color="text.grey" fontWeight={600} fontSize="xs"
                                        textTransform="uppercase" letterSpacing="wider"
                                        py={3} px={5} border="none" textAlign="right"
                                    >
                                        {t("candidatePage.columnActions")}
                                    </Table.ColumnHeader>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {displayCandidates.length === 0 && !isCandidatesLoading && (
                                    <Table.Row bg="bg.secondary">
                                        <Table.Cell colSpan={12} py={10} border="none" textAlign="center">
                                            <Text color="text.grey" fontSize="sm">{t("candidatePage.noCandidates")}</Text>
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                                {displayCandidates.map((cand) => {
                                    const lastNotePreview = cand.lastNote?.content
                                        ? (cand.lastNote.content.length > 60 ? cand.lastNote.content.slice(0, 60) + "…" : cand.lastNote.content)
                                        : null;
                                    const lastEvent = cand.lastTimelineEvent ?? null;
                                    const lastEventBadge = lastEvent ? getTimelineBadgeProps(lastEvent.kind, t) : null;
                                    const lastEventMeta = lastEvent ? parseMetadata(lastEvent.metadata) : null;
                                    const lastEventDescription = lastEvent ? getTimelineEventDescription(lastEvent, t) : null;
                                    const lastEventMetaDisplay = lastEvent ? formatMetadataDisplay(lastEvent, t) : null;
                                    const lastEventNoteDisplay = lastEvent && (lastEvent.kind || '').toLowerCase() === 'statuschange'
                                        ? (lastEventMeta?.note ?? lastEventMeta?.Note ?? null) : null;
                                    return (
                                        <Table.Row
                                            key={cand.id}
                                            bg="bg.secondary"
                                            borderBottom="1px solid"
                                            borderColor="border.primary"
                                            _hover={{ bg: "bg.highlight" }}
                                            transition="background 0.15s"
                                            onClick={() =>
                                                cand.id !== "mock-preview" &&
                                                navigate(`/models/${id}/recruitments/${recruitmentId}/candidates/${cand.id}`)
                                            }
                                            cursor="pointer"
                                        >
                                            <Table.Cell py={4} px={5} border="none" w="40px" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox.Root
                                                    checked={selectedIds.has(cand.id)}
                                                    onCheckedChange={() => toggleSelect(cand.id)}
                                                    disabled={cand.id === "mock-preview"}
                                                    aria-label="Select candidate"
                                                >
                                                    <Checkbox.HiddenInput />
                                                    <Checkbox.Control>
                                                        <Checkbox.Indicator />
                                                    </Checkbox.Control>
                                                </Checkbox.Root>
                                            </Table.Cell>
                                            <Table.Cell py={4} px={2} border="none" w="40px" onClick={(e) => e.stopPropagation()}>
                                                <IconButton
                                                    variant="ghost"
                                                    size="sm"
                                                    aria-label="Ulubione"
                                                    color={cand.isFavourite ? "yellow.400" : "gray.500"}
                                                    loading={candFavLoadingId === cand.id}
                                                    onClick={(e) => handleToggleCandidateFavorite(e, cand.id)}
                                                >
                                                    <HiStar />
                                                </IconButton>
                                            </Table.Cell>
                                            <Table.Cell py={4} px={5} border="none">
                                                <Text color="white" fontSize="sm">
                                                    {[cand.firstname, cand.lastname].filter(Boolean).join(" ") || "-"}
                                                </Text>
                                            </Table.Cell>
                                            <Table.Cell py={4} px={5} border="none" onClick={(e) => e.stopPropagation()}>
                                                <Box as="button" cursor="pointer" transition="opacity 0.15s"
                                                    onClick={() => openStatusModal(cand.id, cand.status)} outline="none">
                                                    <CandidateStatusBadge status={cand.status} />
                                                </Box>
                                            </Table.Cell>
                                            {SCORING_ENABLED && (
                                                <Table.Cell py={4} px={5} border="none">
                                                    <ScoreBar evaluation={toEvaluationBucketShape(cand.evaluation)} />
                                                </Table.Cell>
                                            )}
                                            <Table.Cell py={4} px={5} border="none">
                                                <Text color="white" fontSize="sm">{cand.email ?? "-"}</Text>
                                            </Table.Cell>
                                            <Table.Cell py={4} px={5} border="none" onClick={(e) => e.stopPropagation()}>
                                                {cand.phoneNumber
                                                    ? <Link href={`tel:${cand.phoneNumber}`} color="white" fontSize="sm">{cand.phoneNumber}</Link>
                                                    : <Text color="white" fontSize="sm">-</Text>}
                                            </Table.Cell>
                                            <Table.Cell py={4} px={5} border="none">
                                                <Text color="white" fontSize="sm">{cand.language ?? "-"}</Text>
                                            </Table.Cell>
                                            <Table.Cell py={4} px={5} border="none">
                                                <Text color="white" fontSize="sm">
                                                    {dayjs(cand.createdAt).format('DD.MM.YYYY HH:mm')}
                                                </Text>
                                            </Table.Cell>
                                            <Table.Cell py={4} px={5} border="none" onClick={(e) => e.stopPropagation()}>
                                                {lastEvent ? (
                                                    <Popover.Root positioning={{ placement: 'bottom-start' }}>
                                                        <Popover.Trigger asChild>
                                                            <Box
                                                                as="button"
                                                                cursor="pointer"
                                                                textAlign="left"
                                                                _hover={{ opacity: 0.8 }}
                                                            >
                                                                <Text color="white" fontSize="sm">
                                                                    {dayjs(cand.updatedAt).format('DD.MM.YYYY HH:mm')}
                                                                </Text>
                                                            </Box>
                                                        </Popover.Trigger>
                                                        <Portal>
                                                            <Popover.Positioner>
                                                                <Popover.Content
                                                                    bg="bg.card"
                                                                    border="1px solid"
                                                                    borderColor="border.primary"
                                                                    borderRadius={10}
                                                                    p={4}
                                                                    minW="280px"
                                                                    maxW="400px"
                                                                    boxShadow="0 4px 24px rgba(0,0,0,0.18)"
                                                                >
                                                                    <VStack align="flex-start" gap={2}>
                                                                        <HStack justify="space-between" w="full" flexWrap="wrap" gap={1}>
                                                                            <HStack gap={2}>
                                                                                <Badge
                                                                                    variant="subtle"
                                                                                    colorPalette={lastEventBadge.colorPalette}
                                                                                    borderRadius={6}
                                                                                    fontSize="xs"
                                                                                    fontWeight={600}
                                                                                >
                                                                                    {lastEventBadge.label}
                                                                                </Badge>
                                                                                <Text color="text.grey" fontSize="xs">{lastEvent.authorFullName ?? "System"}</Text>
                                                                            </HStack>
                                                                            <Text color="text.grey" fontSize="xs">
                                                                                {dayjs(lastEvent.occurredAt).format("DD.MM.YYYY HH:mm")}
                                                                            </Text>
                                                                        </HStack>
                                                                        {lastEventDescription && (
                                                                            <Text color="white" fontSize="sm">{lastEventDescription}</Text>
                                                                        )}
                                                                        {lastEventMetaDisplay && (
                                                                            <Box bg="bg.secondary" borderRadius={8} px={3} py={2} w="full">
                                                                                <Text fontFamily="mono" fontSize="xs" color="text.grey">{lastEventMetaDisplay}</Text>
                                                                            </Box>
                                                                        )}
                                                                        {lastEventNoteDisplay && (
                                                                            <Text color="white" fontSize="sm" fontStyle="italic">Notatka: {lastEventNoteDisplay}</Text>
                                                                        )}
                                                                        <Button
                                                                            fontSize={12}
                                                                            px={2}
                                                                            py={0}
                                                                            lineHeight={'12px'}
                                                                            variant="outlined"
                                                                            alignSelf="flex-end"
                                                                            onClick={() => navigate(
                                                                                `/models/${id}/recruitments/${recruitmentId}/candidates/${cand.id}`,
                                                                                { state: { tab: 'timeline', highlightId: lastEvent?.id ?? null } }
                                                                            )}
                                                                        >
                                                                            {t("candidatePage.goToTimeline")}
                                                                        </Button>
                                                                    </VStack>
                                                                </Popover.Content>
                                                            </Popover.Positioner>
                                                        </Portal>
                                                    </Popover.Root>
                                                ) : (
                                                    <Text color="white" fontSize="sm">
                                                        {dayjs(cand.updatedAt).format('DD.MM.YYYY HH:mm')}
                                                    </Text>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell py={4} px={5} border="none" onClick={(e) => e.stopPropagation()}>
                                                {cand.lastNote ? (
                                                    <Popover.Root positioning={{ placement: 'bottom-start' }}>
                                                        <Popover.Trigger asChild>
                                                            <Box
                                                                as="button"
                                                                cursor="pointer"
                                                                textAlign="left"
                                                                _hover={{ opacity: 0.8 }}
                                                            >
                                                                <Text color="text.grey" fontSize="sm">{lastNotePreview}</Text>
                                                            </Box>
                                                        </Popover.Trigger>
                                                        <Portal>
                                                            <Popover.Positioner>
                                                                <Popover.Content
                                                                    bg="bg.card"
                                                                    border="1px solid"
                                                                    borderColor="border.primary"
                                                                    borderRadius={10}
                                                                    p={4}
                                                                    minW="280px"
                                                                    maxW="360px"
                                                                    boxShadow="0 4px 24px rgba(0,0,0,0.18)"
                                                                >
                                                                    <VStack align="flex-start" gap={3}>
                                                                        <VStack align="flex-start" gap={1} w="full">
                                                                            <Text color="text.grey" fontSize="xs" fontWeight={600} textTransform="uppercase">
                                                                                {t("candidatePage.lastNoteContent")}
                                                                            </Text>
                                                                            <Text color="white" fontSize="sm">{cand.lastNote.content}</Text>
                                                                        </VStack>
                                                                        <HStack justify="space-between" w="full" gap={4}>
                                                                            <VStack align="flex-start" gap={0}>
                                                                                <Text color="text.grey" fontSize="xs">{t("candidatePage.lastNoteAuthor")}</Text>
                                                                                <Text color="white" fontSize="xs">{cand.lastNote.authorFullName}</Text>
                                                                            </VStack>
                                                                            <VStack align="flex-start" gap={0}>
                                                                                <Text color="text.grey" fontSize="xs">{t("candidatePage.lastNoteDate")}</Text>
                                                                                <Text color="white" fontSize="xs">
                                                                                    {dayjs(cand.lastNote.createdAt).format('DD.MM.YYYY HH:mm')}
                                                                                </Text>
                                                                            </VStack>
                                                                        </HStack>
                                                                        <HStack justify="space-between" w="full">
                                                                            <Text color="text.grey" fontSize="xs">
                                                                                {t("candidatePage.lastNoteTotal", { count: cand.lastNote.totalNotes })}
                                                                            </Text>
                                                                            <Button
                                                                                fontSize={12}
                                                                                px={2}
                                                                                py={0}
                                                                                lineHeight={'12px'}
                                                                                variant="outlined"
                                                                                onClick={() => navigate(
                                                                                    `/models/${id}/recruitments/${recruitmentId}/candidates/${cand.id}`,
                                                                                    { state: { tab: 'notes', highlightId: cand.lastNote?.id ?? null } }
                                                                                )}
                                                                            >
                                                                                {t("candidatePage.goToNotes")}
                                                                            </Button>
                                                                        </HStack>
                                                                    </VStack>
                                                                </Popover.Content>
                                                            </Popover.Positioner>
                                                        </Portal>
                                                    </Popover.Root>
                                                ) : (
                                                    <Text color="text.grey" fontSize="sm">-</Text>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell py={4} px={5} border="none" textAlign="right" onClick={(e) => e.stopPropagation()}>
                                                <Menu.Root>
                                                    <Menu.Trigger asChild>
                                                        <IconButton variant="outlined" size="sm" aria-label={t("candidatePage.columnActions")}>
                                                            <HiEllipsisVertical />
                                                        </IconButton>
                                                    </Menu.Trigger>
                                                    <Portal>
                                                        <Menu.Positioner>
                                                            <Menu.Content bg="bg.card" border="1px solid" borderColor="border.primary">
                                                                <Menu.Item
                                                                    value="email"
                                                                    disabled={!cand.email}
                                                                    onClick={() => openEmailModal(cand.id)}
                                                                >
                                                                    {t("candidateDetailPage.sendMessage")}
                                                                </Menu.Item>
                                                                <Menu.Item
                                                                    value="status"
                                                                    onClick={() => openStatusModal(cand.id, cand.status)}
                                                                >
                                                                    {t("candidateDetailPage.changeStatus")}
                                                                </Menu.Item>
                                                                <Menu.Item
                                                                    value="note"
                                                                    onClick={() => openNoteModal(cand.id)}
                                                                >
                                                                    {t("candidateDetailPage.addNote")}
                                                                </Menu.Item>
                                                            </Menu.Content>
                                                        </Menu.Positioner>
                                                    </Portal>
                                                </Menu.Root>
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table.Root>
                    </Box>

                    {/* Mobile card list */}
                    <VStack
                        display={{ base: 'flex', md: 'none' }}
                        w="full"
                        gap={2}
                        opacity={isCandidatesLoading ? 0.4 : 1}
                        transition="opacity 0.15s"
                    >
                        {displayCandidates.length === 0 && !isCandidatesLoading && (
                            <Box
                                w="full"
                                bg="bg.card"
                                border="1px solid"
                                borderColor="border.primary"
                                borderRadius={10}
                                p={6}
                                textAlign="center"
                            >
                                <Text color="text.grey" fontSize="sm">{t("candidatePage.noCandidates")}</Text>
                            </Box>
                        )}
                        {displayCandidates.map((cand) => (
                            <Box
                                key={cand.id}
                                w="full"
                                bg="bg.card"
                                border="1px solid"
                                borderColor="border.primary"
                                borderRadius={10}
                                p={3}
                                cursor="pointer"
                                onClick={() => cand.id !== "mock-preview" && navigate(`/models/${id}/recruitments/${recruitmentId}/candidates/${cand.id}`)}
                                _active={{ opacity: 0.8 }}
                            >
                                <HStack justify="space-between" align="flex-start">
                                    <HStack gap={2} flex={1} minW={0}>
                                        <IconButton
                                            variant="ghost"
                                            size="sm"
                                            aria-label="Ulubione"
                                            color={cand.isFavourite ? "yellow.400" : "gray.500"}
                                            loading={candFavLoadingId === cand.id}
                                            flexShrink={0}
                                            onClick={(e) => handleToggleCandidateFavorite(e, cand.id)}
                                        >
                                            <HiStar />
                                        </IconButton>
                                        <VStack align="flex-start" gap={1} flex={1} minW={0}>
                                            <Text color="white" fontSize="sm" fontWeight={600}>
                                                {[cand.firstname, cand.lastname].filter(Boolean).join(" ") || "-"}
                                            </Text>
                                            {cand.email && (
                                                <Text color="text.grey" fontSize="xs">{cand.email}</Text>
                                            )}
                                            {cand.phoneNumber && (
                                                <Link href={`tel:${cand.phoneNumber}`} color="text.grey" fontSize="xs" onClick={(e) => e.stopPropagation()}>
                                                    {cand.phoneNumber}
                                                </Link>
                                            )}
                                            <Text color="text.grey" fontSize="xs" whiteSpace="nowrap">
                                                {dayjs(cand.createdAt).format('DD.MM.YYYY HH:mm')}
                                            </Text>
                                            <Text color="text.grey" fontSize="xs" whiteSpace="nowrap">
                                                {t("candidatePage.columnLastActivity")}: {dayjs(cand.updatedAt).format('DD.MM.YYYY HH:mm')}
                                            </Text>
                                        </VStack>
                                    </HStack>
                                    <VStack gap={1} align="flex-end" flexShrink={0} onClick={(e) => e.stopPropagation()}>
                                        <Box as="button" cursor="pointer" transition="opacity 0.15s"
                                            onClick={() => openStatusModal(cand.id, cand.status)} outline="none">
                                            <CandidateStatusBadge status={cand.status} />
                                        </Box>
                                        {SCORING_ENABLED && (
                                            <ScoreBar evaluation={toEvaluationBucketShape(cand.evaluation)} width="64px" />
                                        )}
                                    </VStack>
                                </HStack>
                            </Box>
                        ))}
                    </VStack>
                </Box>

                {totalCount > 0 && (
                    <VStack w="full" gap={2} pt={1}>
                        <HStack justify="space-between" w="full">
                            <HStack gap={2}>
                                <Text color="text.grey" fontSize="sm" display={{ base: 'none', md: 'block' }}>
                                    {t("candidatePage.paginationRowsPerPage")}
                                </Text>
                                <select
                                    value={pageSize}
                                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    style={{
                                        background: "var(--chakra-colors-bg-card)",
                                        border: "1px solid var(--chakra-colors-border-primary)",
                                        borderRadius: "6px",
                                        color: "white",
                                        fontSize: "var(--chakra-fontSizes-sm)",
                                        padding: "3px 8px",
                                        cursor: "pointer",
                                    }}
                                >
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <Text color="text.grey" fontSize="sm">
                                    {t("candidatePage.paginationInfo", {
                                        page,
                                        totalPages,
                                        totalCount,
                                    })}
                                </Text>
                            </HStack>
                            {totalPages > 1 && (
                                <HStack gap={2}>
                                    <Button
                                        variant="outlined"
                                        size="sm"
                                        onClick={() => setPage((p) => p - 1)}
                                        disabled={page <= 1}
                                    >
                                        {t("candidatePage.paginationPrev")}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="sm"
                                        onClick={() => setPage((p) => p + 1)}
                                        disabled={page >= totalPages}
                                    >
                                        {t("candidatePage.paginationNext")}
                                    </Button>
                                </HStack>
                            )}
                        </HStack>
                    </VStack>
                )}
            </VStack>
        </VStack>
        <BulkChangeStatusModal
            isOpen={showBulkStatusModal}
            onClose={() => setShowBulkStatusModal(false)}
            count={selectedIds.size}
            onSubmit={handleBulkChangeStatus}
            isLoading={isBulkStatusLoading}
        />
        <BulkEmailModal
            isOpen={showBulkEmailModal}
            onClose={() => setShowBulkEmailModal(false)}
            count={selectedIds.size}
            onSubmit={handleBulkSendEmail}
            isLoading={isBulkEmailLoading}
        />
        {singleActionModals}
        </>
    );
};

export default RecruitmentCandidatesPage;
